import React, { useEffect, useState, useCallback, useRef } from "react";
import { auth, db } from "@/config/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { cn } from "@/lib/utils";
import { OddsBadge, CoinDisplay, WalletSummary } from "./OddsBadge";
import { 
  Wallet, TrendingUp, TrendingDown, Clock, CheckCircle, 
  XCircle, MinusCircle, ChevronRight, History 
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Portfolio - User's complete portfolio view
 * 
 * Realtime updates:
 * - Wallet balance updates via Firestore onSnapshot
 * - Full portfolio refetch when wallet changes (openBets, settledBets, etc.)
 */
export function Portfolio({ className }) {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('open'); // 'open', 'settled', 'history'
  const isMountedRef = useRef(true);
  const lastFetchRef = useRef(0);

  // Fetch full portfolio from API
  const fetchPortfolio = useCallback(async (skipThrottle = false) => {
    // Throttle fetches to max once per second (unless skipThrottle)
    const now = Date.now();
    if (!skipThrottle && now - lastFetchRef.current < 1000) {
      return;
    }
    lastFetchRef.current = now;

    try {
      const user = auth.currentUser;
      if (!user) {
        if (isMountedRef.current) {
          setError("Nicht angemeldet");
          setLoading(false);
        }
        return;
      }

      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/api/economy/portfolio`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);
      
      const data = await res.json();
      if (isMountedRef.current) {
        setPortfolio(data);
        setLoading(false);
        setError(null);
      }
    } catch (err) {
      console.error("Portfolio fetch error:", err);
      if (isMountedRef.current) {
        setError(err.message);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial fetch
    fetchPortfolio(true);

    // Set up realtime wallet listener
    const user = auth.currentUser;
    let unsubWallet = null;
    
    if (user) {
      const walletRef = doc(db, "prediction_wallets", user.uid);
      unsubWallet = onSnapshot(walletRef, (snap) => {
        if (!isMountedRef.current) return;
        
        if (snap.exists()) {
          const walletData = snap.data();
          
          // Update wallet using functional state update (avoids stale closure)
          setPortfolio(prev => {
            if (!prev) return prev;
            
            const newWallet = {
              balance: walletData.balance || 0,
              reserved: walletData.reserved || 0,
              available: (walletData.balance || 0) - (walletData.reserved || 0),
              locked: walletData.lockedBalance || 0,
              earned: walletData.earnedBalance || 0,
              score: (walletData.balance || 0) + (walletData.lockedBalance || 0) + (walletData.earnedBalance || 0),
            };
            
            // Check if wallet actually changed
            const walletChanged = 
              prev.wallet.balance !== newWallet.balance ||
              prev.wallet.reserved !== newWallet.reserved ||
              prev.wallet.earned !== newWallet.earned ||
              prev.wallet.locked !== newWallet.locked;
            
            if (walletChanged) {
              // Trigger full refetch to update openBets, settledBets, etc.
              // Use setTimeout to avoid state update during render
              setTimeout(() => fetchPortfolio(), 100);
            }
            
            return {
              ...prev,
              wallet: newWallet,
            };
          });
        }
      }, (err) => {
        console.error("Wallet subscription error:", err);
      });
    }

    return () => {
      isMountedRef.current = false;
      if (unsubWallet) unsubWallet();
    };
  }, [fetchPortfolio]);

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-12", className)}>
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("text-red-400 text-center py-8", className)}>
        <p>{error}</p>
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div className={cn("text-white/50 text-center py-8", className)}>
        Portfolio nicht verfügbar
      </div>
    );
  }

  const { wallet, summary, openBets, settledBets, transactions } = portfolio;

  return (
    <div className={cn("space-y-6", className)}>
      {/* Wallet Summary Card */}
      <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/10 rounded-2xl p-5 border border-yellow-500/30">
        <div className="flex items-center gap-3 mb-4">
          <Wallet className="w-6 h-6 text-yellow-400" />
          <h2 className="text-xl font-bold text-white">Dein Wallet</h2>
        </div>
        
        <div className="text-center mb-4">
          <div className="text-sm text-white/60 mb-1">GESAMT (Ranking)</div>
          <CoinDisplay amount={wallet.score ?? wallet.balance} size="lg" />
        </div>
        
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-black/20 rounded-lg p-3">
            <div className="text-xs text-green-400/80 mb-1">WETT-COINS</div>
            <CoinDisplay amount={wallet.available} size="md" />
            <div className="text-[10px] text-white/40 mt-1">verwettbar</div>
          </div>
          <div className="bg-black/20 rounded-lg p-3">
            <div className="text-xs text-white/50 mb-1">RESERVIERT</div>
            <CoinDisplay amount={wallet.reserved} size="md" />
            <div className="text-[10px] text-white/40 mt-1">in Wetten</div>
          </div>
          <div className="bg-black/20 rounded-lg p-3">
            <div className="text-xs text-purple-300/80 mb-1">🔒 GEWINNE</div>
            <CoinDisplay amount={wallet.locked ?? 0} size="md" />
            <div className="text-[10px] text-white/40 mt-1">gesperrt</div>
          </div>
        </div>

        {(wallet.earned ?? 0) > 0 && (
          <div className="mt-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3 flex items-center justify-center gap-2" data-testid="portfolio-earned">
            <CoinDisplay amount={wallet.earned} size="sm" />
            <span className="text-[12px] text-indigo-100/80">verdiente Coins (Training/Spiel) — <b>ab Phase 2 wettbar</b></span>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-green-400 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs">Gewonnen</span>
          </div>
          <CoinDisplay amount={summary.totalWon} showSign />
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-400 mb-1">
            <TrendingDown className="w-4 h-4" />
            <span className="text-xs">Verloren</span>
          </div>
          <CoinDisplay amount={-summary.totalLost} showSign />
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-yellow-400 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs">Offene Einsätze</span>
          </div>
          <CoinDisplay amount={summary.openStake} />
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-blue-400 mb-1">
            <span className="text-xs">Möglicher Payout</span>
          </div>
          <CoinDisplay amount={summary.openPotentialPayout} />
        </div>
      </div>

      {/* P/L Summary */}
      <div className="bg-white/5 rounded-xl p-4 text-center">
        <div className="text-sm text-white/60 mb-1">Realisierter Gewinn/Verlust</div>
        <CoinDisplay amount={summary.realizedProfitLoss} showSign size="lg" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-black/30 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('open')}
          className={cn(
            "flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all",
            activeTab === 'open' 
              ? "bg-yellow-500/30 text-yellow-300" 
              : "text-white/60 hover:text-white"
          )}
        >
          Offen ({openBets.length})
        </button>
        <button
          onClick={() => setActiveTab('settled')}
          className={cn(
            "flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all",
            activeTab === 'settled' 
              ? "bg-yellow-500/30 text-yellow-300" 
              : "text-white/60 hover:text-white"
          )}
        >
          Abgeschlossen
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={cn(
            "flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all",
            activeTab === 'history' 
              ? "bg-yellow-500/30 text-yellow-300" 
              : "text-white/60 hover:text-white"
          )}
        >
          Historie
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'open' && (
        <BetList bets={openBets} type="open" />
      )}
      {activeTab === 'settled' && (
        <BetList bets={settledBets} type="settled" />
      )}
      {activeTab === 'history' && (
        <TransactionList transactions={transactions} />
      )}
    </div>
  );
}

/**
 * BetList - Display list of bets (open or settled)
 */
function BetList({ bets, type }) {
  if (bets.length === 0) {
    return (
      <div className="text-center py-8 text-white/50">
        {type === 'open' ? 'Keine offenen Wetten' : 'Keine abgeschlossenen Wetten'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {bets.map((bet) => (
        <BetCard key={bet.id} bet={bet} />
      ))}
    </div>
  );
}

/**
 * BetCard - Single bet display
 */
function BetCard({ bet }) {
  const statusConfig = {
    PENDING: { icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/10", label: "OFFEN" },
    WON: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/10", label: "GEWONNEN" },
    LOST: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10", label: "VERLOREN" },
    VOID: { icon: MinusCircle, color: "text-gray-400", bg: "bg-gray-500/10", label: "ABGEBROCHEN" },
  };

  const config = statusConfig[bet.status] || statusConfig.PENDING;
  const StatusIcon = config.icon;

  return (
    <div className={cn(
      "rounded-xl border p-4",
      config.bg,
      bet.status === 'PENDING' ? "border-yellow-500/30" : "border-white/10"
    )}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-medium text-white">{bet.title}</div>
          <div className="flex items-center gap-2 mt-1">
            <span className={cn(
              "px-2 py-0.5 rounded text-xs font-medium",
              bet.side === 'YES' ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
            )}>
              {bet.side === 'YES' ? 'JA' : 'NEIN'}
            </span>
            <OddsBadge value={bet.odds} mode="FIXED" size="sm" />
          </div>
        </div>
        <div className={cn("flex items-center gap-1", config.color)}>
          <StatusIcon className="w-4 h-4" />
          <span className="text-xs font-medium">{config.label}</span>
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-white/50">Einsatz: </span>
          <CoinDisplay amount={bet.stake} size="sm" />
        </div>
        <div>
          <span className="text-white/50">
            {bet.status === 'PENDING' ? 'Möglicher Gewinn: ' : 'Auszahlung: '}
          </span>
          <CoinDisplay amount={bet.status === 'PENDING' ? bet.potentialPayout : (bet.payout || 0)} size="sm" />
        </div>
      </div>

      {/* Net Result for settled bets */}
      {bet.status !== 'PENDING' && bet.netResult !== null && (
        <div className="mt-3 pt-3 border-t border-white/10 text-right">
          <span className="text-white/50 text-sm mr-2">Netto:</span>
          <CoinDisplay amount={bet.netResult} showSign size="md" />
        </div>
      )}
    </div>
  );
}

/**
 * TransactionList - Display transaction history
 */
function TransactionList({ transactions }) {
  // Map transaction types to user-friendly labels
  const typeLabels = {
    'SEASON_START': 'Startkapital',
    'WHO_IS_IT_CORRECT': 'WHO IS IT Richtig',
    'WHO_IS_IT': 'WHO IS IT',
    'PROBABILITY_CORRECT': 'Wahrscheinlichkeit',
    'CHALLENGE_WIN': 'Challenge Gewinn',
    'CHALLENGE_LOSS': 'Challenge Verlust',
    'CHALLENGE_VOID': 'Challenge Abbruch',
    'CHALLENGER_BONUS': 'Challenger Bonus',
    'ADMIN_ADJUST': 'Korrektur durch Spielleiter',
  };

  if (transactions.length === 0) {
    return (
      <div className="text-center py-8 text-white/50">
        Keine Transaktionen
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {transactions.map((txn) => (
        <div
          key={txn.id}
          className="flex items-center justify-between py-3 px-4 bg-white/5 rounded-lg"
        >
          <div className="flex items-center gap-3">
            <History className="w-4 h-4 text-white/40" />
            <div>
              <div className="text-sm text-white">
                {typeLabels[txn.type] || txn.reason || txn.type}
              </div>
              {txn.reason && typeLabels[txn.type] && txn.reason !== typeLabels[txn.type] && (
                <div className="text-xs text-white/60 italic">{txn.reason}</div>
              )}
              {txn.createdAt && (
                <div className="text-xs text-white/40">
                  {new Date(txn.createdAt).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              )}
            </div>
          </div>
          <CoinDisplay amount={txn.amount} showSign size="md" />
        </div>
      ))}
    </div>
  );
}

/**
 * PortfolioCompact - Compact portfolio summary for headers/sidebars
 */
export function PortfolioCompact({ className }) {
  const [wallet, setWallet] = useState({ balance: 0, reserved: 0, available: 0 });

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const walletRef = doc(db, "prediction_wallets", user.uid);
    const unsubscribe = onSnapshot(walletRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setWallet({
          balance: data.balance || 0,
          reserved: data.reserved || 0,
          available: (data.balance || 0) - (data.reserved || 0),
        });
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Wallet className="w-4 h-4 text-yellow-400" />
      <CoinDisplay amount={wallet.balance} size="sm" />
    </div>
  );
}

export default Portfolio;
